/**
 * The rules engine. Pure functions over (content, progress, day) — no I/O, no dates read from
 * the machine clock, no randomness. The server and the client both run this exact code, so what
 * the student sees and what the server enforces can never drift apart.
 *
 * Two numbers exist in this product and they are both plain Serbian nouns:
 *   radovi      — works posted to the community. This is what opens islands.
 *   popravljeni — works revised after feedback. This is the real progress signal.
 * If a third number appears here, something has gone wrong.
 */

import type { ResolvedContent, ResolvedIsland, ResolvedNode } from './content';
import { counted, WORDS } from './lang';

/** What the database knows about one node, for one player. */
export interface NodeProgress {
  nodeId: string;
  /** Set for nodes that ask for nothing but "I did it". */
  doneAt?: number | null;
  v1Url?: string | null;
  v1At?: number | null;
  /** When feedback was confirmed — by the student, or by a mentor in the admin queue. */
  feedbackAt?: number | null;
  v2Url?: string | null;
  v2At?: number | null;
}

export interface PlayerState {
  /** Days since the Skool membership started, not since the account here was created. */
  day: number;
  path: string;
  progress: Record<string, NodeProgress>;
  /** Islands the player has ever had access to. Access is never taken back. */
  openedIslands: string[];
  /** Islands the player has fully cleared, ever. */
  clearedIslands: string[];
}

export type NodeStatus =
  | 'zakljucan'
  | 'otvoren'
  | 'zavrsen'
  | 'okacen'
  | 'feedback'
  | 'popravljen';

export const ISLAND_CLEAR_THRESHOLD = 0.75;
/** Days a work can sit without feedback before the app suggests asking for it. */
export const FEEDBACK_NUDGE_DAYS = 3;

/* ------------------------------------------------------------------ node status */

export function nodeStatusOf(node: ResolvedNode, progress: NodeProgress | undefined): NodeStatus {
  // Endowed progress: the profile node is counted done the moment the account exists, because it
  // was in fact filled in during registration. Nobody starts this game on 0 of 4 (Nunes & Drèze).
  if (!progress) return node.preCompleted && node.submission === 'nista' ? 'zavrsen' : 'otvoren';
  if (node.submission === 'nista') return progress.doneAt ? 'zavrsen' : 'otvoren';
  if (progress.v2At) return 'popravljen';
  if (progress.v1At) {
    if (!node.needsRevision) return 'zavrsen';
    return progress.feedbackAt ? 'feedback' : 'okacen';
  }
  return 'otvoren';
}

/**
 * "Complete" for the purpose of island progress.
 *
 * A big task counts only once it has been revised. That is the single place in the system where
 * the revision is mechanically worth more than the first attempt — it is what turns the island
 * green and hands over the souvenir. It never hard-blocks anyone, because the next island opens
 * at 75%, so a student who never revises still moves on; they just do not clear the island.
 */
export function isNodeComplete(node: ResolvedNode, progress: NodeProgress | undefined): boolean {
  const status = nodeStatusOf(node, progress);
  return status === 'zavrsen' || status === 'popravljen';
}

/** Nodes whose first version is posted but which are not finished. */
export function isNodeInFlight(node: ResolvedNode, progress: NodeProgress | undefined): boolean {
  const status = nodeStatusOf(node, progress);
  return status === 'okacen' || status === 'feedback';
}

/* ------------------------------------------------------------------ the two numbers */

/**
 * Every version posted to the community counts: the first attempt and the revision both.
 *
 * Counted from the progress rows, not from the current content. A work does not un-happen because
 * an admin later deleted the node or the student switched path — „radovi" is a fact about what the
 * person did, and a number the product shows must never go down. `content` stays in the signature:
 * every caller has it, and the day this needs to exclude something it will need it.
 */
export function countWorks(_content: ResolvedContent, state: PlayerState): number {
  let works = 0;
  for (const progress of Object.values(state.progress)) {
    if (progress.v1At) works += 1;
    if (progress.v2At) works += 1;
  }
  return works;
}

export function countRevisions(_content: ResolvedContent, state: PlayerState): number {
  let revisions = 0;
  for (const progress of Object.values(state.progress)) if (progress.v2At) revisions += 1;
  return revisions;
}

/* ------------------------------------------------------------------ islands */

export interface IslandProgress {
  done: number;
  total: number;
  ratio: number;
  cleared: boolean;
}

/**
 * The ordered chain of course islands. A free island is a place you can go, never a step you must
 * take, so it is not in it.
 *
 * Exported and used by every function below that does index arithmetic, so that the "islands are a
 * line" assumption lives in exactly one place and can be audited by grepping for this name. Before
 * this existed the assumption was spread across seven functions, and an island beside the line
 * would have silently become somebody's predecessor.
 *
 * A no-op while no island is free: `chainIslands(content)` is then `content.islands` itself.
 */
export function chainIslands(content: ResolvedContent): ResolvedIsland[] {
  return content.islands.filter((island) => !island.free);
}

/**
 * Optional nodes are bonus: they never sit in the denominator and never block a clear.
 *
 * A free island has no denominator at all, and that one line is the whole defence against the
 * catalogue: „12 od 40" would be a third number, a permanent statement of what you have not done,
 * and a denominator that never falls. It reports `cleared: false` because it genuinely never is —
 * so no souvenir, no `island.cleared`, and no contribution to progressive disclosure.
 */
export function islandProgress(island: ResolvedIsland, state: PlayerState): IslandProgress {
  if (island.free) return { done: 0, total: 0, ratio: 1, cleared: false };
  const required = island.nodes.filter((n) => !n.optional && n.kind !== 'riznica');
  if (required.length === 0) return { done: 0, total: 0, ratio: 1, cleared: true };
  const done = required.filter((n) => isNodeComplete(n, state.progress[n.id])).length;
  const ratio = done / required.length;
  return { done, total: required.length, ratio, cleared: ratio >= 1 };
}

/**
 * The souvenirs this player has earned, in the order the islands grant them.
 *
 * Derived, never stored: a souvenir is the visible face of a cleared island, so the cleared
 * islands *are* the wardrobe and there is nothing new to persist, migrate or desync. The server's
 * `clearedIslands` is the durable record — once earned, never taken back, exactly like access —
 * and the live check alongside it means the cap appears the moment the island clears, even
 * offline, without waiting for the server to say so.
 *
 * This is a list of ids, not a number. Nothing anywhere may display its length.
 */
export function earnedSouvenirs(content: ResolvedContent, state: PlayerState): string[] {
  const out: string[] = [];
  for (const island of content.islands) {
    if (!island.souvenir) continue;
    const progress = islandProgress(island, state);
    // `progress.total > 0` guards the degenerate all-optional island, which reports itself
    // cleared to everyone on day zero — that must never hand out a souvenir.
    if (state.clearedIslands.includes(island.id) || (progress.total > 0 && progress.cleared))
      out.push(island.souvenir);
  }
  return out;
}

export type BlockerKind = 'vreme' | 'rad' | 'redom';

export interface Blocker {
  kind: BlockerKind;
  /** The one line the student sees. Never two numbers at once (GDD §2②). */
  text: string;
  detail?: string;
  /** For 'vreme': days left. For 'rad': works still needed. */
  amount?: number;
}

export function isIslandUnlocked(
  content: ResolvedContent,
  state: PlayerState,
  island: ResolvedIsland,
): boolean {
  // Access, once granted, is permanent. Content edits and path switches must not take it away.
  if (state.openedIslands.includes(island.id)) return true;

  /**
   * A free island has no predecessor, so the third clause simply does not apply to it — two gates,
   * and that is all. Deliberately branched here rather than by hoisting the day/works checks above
   * the `index <= 0` return: that would also change the `index === -1` case, which today means "not
   * on this path" and is answered `true`.
   */
  if (island.free) {
    if (state.day < island.day) return false;
    return countWorks(content, state) >= island.worksRequired;
  }

  const chain = chainIslands(content);
  const index = chain.findIndex((i) => i.id === island.id);
  if (index <= 0) return true;

  if (state.day < island.day) return false;
  if (countWorks(content, state) < island.worksRequired) return false;

  const previous = chain[index - 1]!;
  return islandProgress(previous, state).ratio >= ISLAND_CLEAR_THRESHOLD;
}

/** One key per island. The order here is the rule — time first, then works, then sequence. */
export function islandBlocker(
  content: ResolvedContent,
  state: PlayerState,
  island: ResolvedIsland,
): Blocker | null {
  if (isIslandUnlocked(content, state, island)) return null;

  if (state.day < island.day) {
    const daysLeft = island.day - state.day;
    return {
      kind: 'vreme',
      text: `Otvara se ${island.day}. dana`,
      detail: daysLeft === 1 ? 'sutra' : `za ${counted(daysLeft, WORDS.dan)}`,
      amount: daysLeft,
    };
  }

  const missing = island.worksRequired - countWorks(content, state);
  if (missing > 0) {
    // Through counted(), not by hand: islands 4-8 ask for 6, 9, 12, 15 and 18 works, and every one
    // of those needs the "many" form. "Okači još 6 rada" is the exact register of a bad translation.
    return {
      kind: 'rad',
      text: `Okači još ${counted(missing, WORDS.rad)}`,
      amount: missing,
    };
  }

  // The previous CHAIN island. A free island is nobody's predecessor, and a card reading „Završi
  // prethodno ostrvo · VEŽBALIŠTE" would be telling somebody to finish a place that cannot be
  // finished. (A free island never reaches this line: it has only the two gates above.)
  const chain = chainIslands(content);
  const index = chain.findIndex((i) => i.id === island.id);
  const previous = index > 0 ? chain[index - 1] : undefined;
  return {
    kind: 'redom',
    text: 'Završi prethodno ostrvo',
    detail: previous ? previous.name : undefined,
  };
}

/**
 * What the map shows: every island the student can reach, plus exactly one more. Beyond that is
 * open sea — not grey shapes, not locked icons, nothing (GDD §3).
 */
export function visibleIslands(content: ResolvedContent, state: PlayerState): ResolvedIsland[] {
  // The horizon is a property of the chain: one look-ahead along the line you are walking. A free
  // island neither occupies that slot nor consumes it — it appears when it opens and not before.
  const chain = chainIslands(content);
  let lastUnlocked = 0;
  chain.forEach((island, index) => {
    if (isIslandUnlocked(content, state, island)) lastUnlocked = index;
  });

  const shown = new Set(chain.slice(0, lastUnlocked + 2).map((i) => i.id));
  for (const island of content.islands)
    if (island.free && isIslandUnlocked(content, state, island)) shown.add(island.id);

  // Returned in content order, because `buildWorld` and the engine resolve overlapping islands by
  // array order and both would disagree with the map if this reordered them.
  return content.islands.filter((island) => shown.has(island.id));
}

/**
 * The island the student is standing on — which is where their next piece of work is, not the
 * earliest one they left at 80%.
 *
 * The 75% threshold exists precisely so people move on with a lesson or two unfinished behind
 * them (GDD §4). Anchoring "where you are" to the first island that is not 100% done would leave
 * the header naming an island they deliberately walked away from a month ago.
 */
export function currentIsland(content: ResolvedContent, state: PlayerState): ResolvedIsland {
  /**
   * Where you are is a position on the chain. A free island always has open work on it by
   * construction, so scanning it here would make it "where you are" the moment everything else is
   * finished — and this value names the header, picks the live room, and labels the map's list
   * button. You visit a workshop; you are not *at* one.
   */
  const chain = chainIslands(content);
  const unlocked = chain.filter((i) => isIslandUnlocked(content, state, i));
  if (unlocked.length === 0) return chain[0] ?? content.islands[0]!;

  // The furthest island with anything still open on it.
  for (let index = unlocked.length - 1; index >= 0; index--) {
    const island = unlocked[index]!;
    const hasOpenWork = island.nodes.some((node) => {
      if (node.optional || node.kind === 'riznica') return false;
      const status = nodeStatusOf(node, state.progress[node.id]);
      return status !== 'zavrsen' && status !== 'popravljen';
    });
    if (hasOpenWork) return island;
  }
  return unlocked[unlocked.length - 1]!;
}

/* ------------------------------------------------------------------ nodes */

/**
 * A node opens when its prerequisites are done — with exactly one step of slack, so a student
 * can skip a lesson they already know without getting stuck. Same reasoning as the 75% island
 * threshold: leave room to skip one, not to skip everything.
 */
export function isNodeUnlocked(
  content: ResolvedContent,
  state: PlayerState,
  island: ResolvedIsland,
  node: ResolvedNode,
): boolean {
  if (!isIslandUnlocked(content, state, island)) return false;
  if (node.kind === 'riznica') return true;

  const byId = new Map(island.nodes.map((n) => [n.id, n]));
  const unmet = node.requires.filter((id) => {
    const required = byId.get(id);
    if (!required) return false; // requirement filtered out for this path — treat as met
    return !isNodeComplete(required, state.progress[id]);
  });

  if (unmet.length === 0) return true;
  if (unmet.length > 1) return false;

  // One step of slack: the single missing prerequisite must itself be reachable.
  const only = byId.get(unmet[0]!);
  if (!only) return true;
  return only.requires.every((id) => {
    const deeper = byId.get(id);
    return !deeper || isNodeComplete(deeper, state.progress[id]);
  });
}

export interface NodeView {
  node: ResolvedNode;
  island: ResolvedIsland;
  status: NodeStatus;
  unlocked: boolean;
  progress: NodeProgress | undefined;
  /** Minutes this specific action takes right now — a revision is not a fresh attempt. */
  minutes: number;
}

export function revisionMinutes(node: ResolvedNode): number {
  return Math.max(15, Math.round(node.estMin * 0.5));
}

export function viewNode(
  content: ResolvedContent,
  state: PlayerState,
  island: ResolvedIsland,
  node: ResolvedNode,
): NodeView {
  const progress = state.progress[node.id];
  const status = nodeStatusOf(node, progress);
  return {
    node,
    island,
    status,
    unlocked: isNodeUnlocked(content, state, island, node),
    progress,
    minutes: status === 'feedback' ? revisionMinutes(node) : node.estMin,
  };
}

/* ------------------------------------------------------------------ the one next step */

export type TimeBudget = 15 | 45 | 120 | 600 | null;
export const TIME_BUDGETS: Array<{ minutes: Exclude<TimeBudget, null>; label: string }> = [
  { minutes: 15, label: '15 min' },
  { minutes: 45, label: '45 min' },
  { minutes: 120, label: '2 sata' },
  { minutes: 600, label: 'ceo dan' },
];

export type NextStep =
  | { kind: 'popravka'; view: NodeView }
  | { kind: 'rad'; view: NodeView }
  /** `offer` turns the lock from a wall into a door: one exercise that raises the works count. */
  | { kind: 'brava'; island: ResolvedIsland; blocker: Blocker; offer: NodeView | null }
  | { kind: 'nista-ne-staje'; budget: number; shortest: NodeView | null }
  | { kind: 'ceka-feedback'; views: NodeView[] }
  | { kind: 'kraj'; offer: NodeView | null };

/**
 * The single card the app opens on.
 *
 * Order is the design, not an implementation detail:
 *   1. a revision that is ready, whenever it fits the time the student says they have (and always
 *      when they have not said) — see the exception spelled out at the top of the function
 *   2. the next lesson, which nothing may displace
 *   3. an exercise, when works are owed and the last thing posted came from a course
 *   4. the first open piece of course work that fits that time
 *   5. the one thing blocking the next island — with an exercise that moves it
 *   6. a work that is out for comment, so "waiting" never reads as "finished"
 */
export function nextStep(
  content: ResolvedContent,
  state: PlayerState,
  budget: TimeBudget = null,
  skip = 0,
): NextStep {
  const openIslands = content.islands.filter((i) => isIslandUnlocked(content, state, i));
  const debt = worksOwed(content, state);
  const offerOne = (): NodeView | null => freeWork(content, state, budget, skip, debt);

  // 1 — a revision beats everything else on the board.
  //
  // With one exception, and it is the honest one: if the student has told us they have fifteen
  // minutes, offering them a two-hour revision is not "the next step", it is a wall. In that case
  // the revision drops to the waiting strip (still visible, still first thing they read) and the
  // card offers something that actually fits. With no budget set, the revision always wins.
  for (const island of openIslands) {
    for (const node of island.nodes) {
      if (nodeStatusOf(node, state.progress[node.id]) !== 'feedback') continue;
      const view = viewNode(content, state, island, node);
      if (budget === null || view.minutes <= budget) return { kind: 'popravka', view };
    }
  }

  // 2 — the next real piece of work, preferring the earliest one that fits.
  //
  // Chain islands only. A free island's nodes are reached through `freeWork`, which offers them
  // for a reason and in a considered order; letting them into this scan would hand out whichever
  // exercise happens to sit first in the array and would do it forever.
  const candidates: NodeView[] = [];
  for (const island of openIslands) {
    if (island.free) continue;
    for (const node of island.nodes) {
      const view = viewNode(content, state, island, node);
      if (!view.unlocked) continue;
      if (view.status !== 'otvoren') continue;
      if (node.kind === 'riznica') continue;
      candidates.push(view);
    }
  }

  const required = candidates.filter((c) => !c.node.optional);
  const pool = required.length > 0 ? required : candidates;

  // Whichever chain task this member would have been given before any of this existed.
  const chosen =
    budget === null
      ? (pool[0] ?? null)
      : (pool.find((c) => c.minutes <= budget) ??
        // Nothing required fits. An open optional node that does is a better answer than "nothing
        // fits" — that is exactly what the optional nodes are for.
        candidates.find((c) => c.minutes <= budget) ??
        null);

  /**
   * The rhythm, and it only ever alternates POSTED WORK.
   *
   * A REQUIRED lesson is never displaced by an exercise: lessons are how the course is learned,
   * they cost nothing in works, and a card that kept offering an exercise while somebody was
   * working through REZ's lesson chain would be nagging — the member would do the lesson from the
   * map and come back to the same unwanted card. So the exercise only ever stands in for the *next
   * thing you would have posted*, which is exactly the "course thing, then a thing you made"
   * cadence the 2026 plan describes, and it self-limits: posting the exercise flips the boolean
   * straight back.
   *
   * An OPTIONAL node is a different thing and may be displaced. It only ever reaches the card when
   * nothing required is left, and at that moment „Kako praviti titlove (detaljno) — opciono" is a
   * worse answer than the exercise the next island is actually waiting on. It does not disappear;
   * it is still on the island and still on the map.
   *
   * And it requires something to alternate WITH. With the chain empty this must fall through to
   * the blocker below, which offers the same exercise and can also say what it opens — a bare
   * exercise card there would be the same work with the reason stripped off it.
   */
  if (chosen && !chosen.node.posts && !chosen.node.optional) return { kind: 'rad', view: chosen };
  if (chosen && exerciseLeads(content, state)) {
    const lead = offerOne();
    if (lead) return { kind: 'rad', view: lead };
  }
  if (chosen) return { kind: 'rad', view: chosen };

  if (candidates.length > 0 && budget !== null) {
    // Something is open on the chain, it is just all too long for the time they said they have.
    // An exercise that fits is a real answer; a shrug is not.
    const free = offerOne();
    if (free) return { kind: 'rad', view: free };
    const shortest =
      [...candidates, ...freeCandidates(content, state, null, debt)].sort(
        (a, b) => a.minutes - b.minutes,
      )[0] ?? null;
    return { kind: 'nista-ne-staje', budget, shortest };
  }

  // 3 — nothing open on the chain: show the one thing standing in the way, and name something that
  // moves it. Chain islands only — a free island is never what stands in anybody's way.
  const locked = chainIslands(content).find((i) => !isIslandUnlocked(content, state, i));
  if (locked) {
    const blocker = islandBlocker(content, state, locked);
    if (blocker) return { kind: 'brava', island: locked, blocker, offer: offerOne() };
  }

  // 4 — everything is unlocked and nothing is open, but a work is still out for comment. Telling
  // that student "you have finished everything" is wrong: they are mid-island, waiting on a person.
  const waiting = awaitingFeedback(content, state);
  if (waiting.length > 0) return { kind: 'ceka-feedback', views: waiting };

  return { kind: 'kraj', offer: offerOne() };
}

/** Works posted and still waiting on a comment. Shown as a strip, never as a second card. */
export function awaitingFeedback(content: ResolvedContent, state: PlayerState): NodeView[] {
  const out: NodeView[] = [];
  for (const island of content.islands) {
    for (const node of island.nodes) {
      if (nodeStatusOf(node, state.progress[node.id]) === 'okacen')
        out.push(viewNode(content, state, island, node));
    }
  }
  return out;
}

/**
 * Every revision that is ready to be made, on an island the student can reach. It takes no budget:
 * the caller decides whether to show one, and Sada.tsx only surfaces these when `nextStep` did not
 * already put a revision on the card.
 */
export function pendingRevisions(content: ResolvedContent, state: PlayerState): NodeView[] {
  const out: NodeView[] = [];
  for (const island of content.islands) {
    if (!isIslandUnlocked(content, state, island)) continue;
    for (const node of island.nodes) {
      if (nodeStatusOf(node, state.progress[node.id]) === 'feedback')
        out.push(viewNode(content, state, island, node));
    }
  }
  return out;
}

/* ------------------------------------------------------------------ the free island */

/** How many exercises stand on the free island at once. Never the catalogue. */
export const FREE_OFFER_COUNT = 3;

/**
 * Whether an exercise is on offer yet.
 *
 * `posle` is an editorial horizon, not a lock: an exercise the 2026 plan puts in week two is simply
 * not offered before then. It reads `openedIslands`, which the server writes with COALESCE and
 * never withdraws, so this is monotone — an exercise that has been offered can never vanish again,
 * least of all one somebody has already started.
 *
 * An id naming an island this path does not have counts as met, exactly like a `zahteva` pointing
 * at a node filtered out for this path.
 */
export function isNodeOffered(
  content: ResolvedContent,
  state: PlayerState,
  node: ResolvedNode,
): boolean {
  if (!node.after) return true;
  if (state.openedIslands.includes(node.after)) return true;
  const target = content.islands.find((i) => i.id === node.after);
  return target ? isIslandUnlocked(content, state, target) : true;
}

/**
 * Every exercise a member could pick up right now, best first.
 *
 * Two ordering rules and nothing else:
 *   - no budget → the authored order. The admin sequenced the island in ⚙ → Svet and that sequence
 *     IS the recommendation, which is where the 2026 plan lives now.
 *   - a budget → the longest one that fits. They said how much time they have; the exercise worth
 *     offering is the one that uses it. Same stance as `nextStep` preferring the first fitting node
 *     over the shortest one.
 *
 * `debt` is the works still owed to the next locked island — the same arithmetic the „Okači još 2
 * rada" blocker already does, not a new quantity. When it is two or more, exercises that end in a
 * revision sort to the front, so the one on offer is the one that closes the whole debt by itself.
 * That steers toward the only number this project measures without printing „vredi više" anywhere.
 */
export function freeCandidates(
  content: ResolvedContent,
  state: PlayerState,
  budget: TimeBudget,
  debt = 1,
): NodeView[] {
  const pool: NodeView[] = [];
  for (const island of content.islands) {
    if (!island.free) continue;
    if (!isIslandUnlocked(content, state, island)) continue;
    for (const node of island.nodes) {
      if (!node.posts) continue;
      if (!isNodeOffered(content, state, node)) continue;
      const view = viewNode(content, state, island, node);
      if (!view.unlocked || view.status !== 'otvoren') continue;
      if (budget !== null && view.minutes > budget) continue;
      pool.push(view);
    }
  }

  const ranked =
    budget === null ? pool : [...pool].sort((a, b) => b.minutes - a.minutes);
  if (debt < 2) return ranked;
  return [...ranked.filter((v) => v.node.needsRevision), ...ranked.filter((v) => !v.node.needsRevision)];
}

/** One exercise. `skip` is DRUGA VEŽBA — a stable walk down the same ranking, never a shuffle. */
export function freeWork(
  content: ResolvedContent,
  state: PlayerState,
  budget: TimeBudget,
  skip = 0,
  debt = 1,
): NodeView | null {
  const ranked = freeCandidates(content, state, budget, debt);
  if (ranked.length === 0) return null;
  return ranked[((skip % ranked.length) + ranked.length) % ranked.length]!;
}

/**
 * Works still owed to the next locked island on the chain.
 *
 * This is `islandBlocker`'s own subtraction (`worksRequired - countWorks`) done one island ahead
 * instead of at the wall — no new quantity, and nothing renders it. It exists so the app can offer
 * an exercise *before* somebody hits „Okači još 2 rada" rather than only after.
 */
export function worksOwed(content: ResolvedContent, state: PlayerState): number {
  for (const island of chainIslands(content)) {
    if (isIslandUnlocked(content, state, island)) continue;
    return Math.max(0, island.worksRequired - countWorks(content, state));
  }
  return 0;
}

/**
 * Whether the one card should hand over an exercise instead of the next course task.
 *
 * The rhythm the 2026 plan describes — do the course thing, make a thing, do the course thing —
 * expressed as a boolean over facts that already exist. It is true only when BOTH hold:
 *
 *   1. works are actually owed further up the chain, so nobody is ever pulled off a course for an
 *      exercise they do not need; and
 *   2. the most recent work they posted came from the curriculum rather than from the free island,
 *      so the two sides alternate instead of one crowding the other out.
 *
 * Lessons write `doneAt`, never `v1At`, so a run of six lessons cannot flip it — only a posted
 * work can. It returns a boolean, counts nothing, and is never displayed.
 */
export function exerciseLeads(content: ResolvedContent, state: PlayerState): boolean {
  if (worksOwed(content, state) === 0) return false;

  let course = 0;
  let free = 0;
  for (const island of content.islands) {
    for (const node of island.nodes) {
      const progress = state.progress[node.id];
      if (!progress) continue;
      const at = Math.max(progress.v1At ?? 0, progress.v2At ?? 0);
      if (!at) continue;
      if (island.free) free = Math.max(free, at);
      else course = Math.max(course, at);
    }
  }
  return course > free;
}

/**
 * What actually stands on an island: everything, unless it is free.
 *
 * On a free island a member sees the few on offer, the footprints of what they have already
 * started or finished, and the noticeboard. The other thirty-odd are not dimmed and not counted —
 * they are absent, which is the same answer the map gives for islands past the horizon.
 *
 * Deliberately independent of the time budget: the island's markers would otherwise rearrange
 * themselves every time somebody taps „15 min", and the map's ground layer is keyed on this set.
 * Geometry still comes from `layoutIds`, so two members on different paths — or one browsing and
 * one working — stand on identical tiles.
 */
export function shownNodes(
  content: ResolvedContent,
  state: PlayerState,
  island: ResolvedIsland,
): ResolvedNode[] {
  if (!island.free) return island.nodes;

  const shown = new Set<string>();
  for (const node of island.nodes) {
    if (node.kind === 'riznica') shown.add(node.id);
    else if (nodeStatusOf(node, state.progress[node.id]) !== 'otvoren') shown.add(node.id);
  }
  for (const view of freeCandidates(content, state, null).slice(0, FREE_OFFER_COUNT))
    if (view.island.id === island.id) shown.add(view.node.id);

  return island.nodes.filter((node) => shown.has(node.id));
}

export function shouldNudgeForFeedback(view: NodeView, now: number): boolean {
  const postedAt = view.progress?.v1At;
  if (!postedAt) return false;
  return now - postedAt >= FEEDBACK_NUDGE_DAYS * 86_400_000;
}

/** What the card may promise a step unlocks. Serbian kinds, like NodeStatus. */
export type StepOpens =
  | { kind: 'prelazi'; island: ResolvedIsland }
  | { kind: 'sledece'; island: ResolvedIsland }
  | null;

/**
 * What completing this node actually unlocks, for the one line under the card.
 *
 * The claim has to be earned, and two kinds of node can never earn it: optional and treasure
 * nodes are excluded from the island's denominator (see islandProgress), so completing one
 * moves nothing — and an island already cleared cannot be cleared again, however many bonus
 * nodes remain on it. A promise like „ovim prelaziš ostrvo" that does not come true is worse
 * than no line at all.
 */
export function stepOpens(
  content: ResolvedContent,
  state: PlayerState,
  island: ResolvedIsland,
  node: ResolvedNode,
): StepOpens {
  if (node.optional || node.kind === 'riznica') return null;
  // A free island is never crossed and has no next island. Without this the line below would read
  // `1 >= 0` on its zero denominator and promise „ovim prelaziš ostrvo" forever.
  if (island.free) return null;
  const progress = islandProgress(island, state);
  if (progress.cleared) return null;
  if (progress.total === 0) return null;
  if (progress.done + 1 >= progress.total) return { kind: 'prelazi', island };
  if (node.kind === 'veliki') {
    const chain = chainIslands(content);
    const index = chain.findIndex((i) => i.id === island.id);
    const next = index >= 0 ? chain[index + 1] : undefined;
    if (next) return { kind: 'sledece', island: next };
  }
  return null;
}

/* ------------------------------------------------------------------ progressive disclosure */

export type Feature = 'sada' | 'mapa' | 'dnevnik' | 'lik' | 'dom' | 'posada' | 'napomene';

/**
 * The navigation grows. A student does not see the whole app on day one — screens appear when
 * there is finally something in them (GDD §3).
 */
export function unlockedFeatures(content: ResolvedContent, state: PlayerState): Feature[] {
  const features: Feature[] = ['sada', 'mapa'];
  if (countWorks(content, state) >= 1) features.push('dnevnik');
  /**
   * `total > 0` matters on its own, separately from free islands: `islandProgress` reports an
   * island with no required nodes as cleared to everybody on day zero, so without this guard a
   * degenerate island hands out LIK — and with it POSADA and NAPOMENE — to a brand-new account.
   * `earnedSouvenirs` has carried the same guard since it was written; this one was missing.
   */
  const cleared = content.islands.filter((i) => {
    const progress = islandProgress(i, state);
    return progress.total > 0 && progress.cleared;
  }).length;
  const everCleared = Math.max(cleared, state.clearedIslands.length);
  if (everCleared >= 1) features.push('lik');
  /**
   * MOJE OSTRVO arrives with LIK, at the first cleared island, and deliberately NOT at the crew's
   * gate one island later.
   *
   * The disclosure table has always promised a character screen here and never had one; this is that
   * door, and it is the same moment for the same reason — somebody who has finished an island has
   * been here long enough for a place of their own to mean something. Hanging it on `posada` instead
   * would give every member between the first island and the second an island nobody can visit, and
   * GDD §1 says that plateau is where most of this community sits.
   */
  if (everCleared >= 1) features.push('dom');
  if (everCleared >= 2) features.push('posada');
  if (everCleared >= 3) features.push('napomene');
  return features;
}

/* ------------------------------------------------------------------ integrity */

/**
 * Progress rows whose node no longer exists in the content. They are never deleted — a student
 * who did the work keeps the credit, it simply stops showing on the map.
 */
export function orphanedProgress(content: ResolvedContent, state: PlayerState): string[] {
  return Object.keys(state.progress).filter((id) => !content.allNodeIds.has(id));
}

/** Everything the shell needs in one pass, so the client never recomputes this twice. */
export interface Overview {
  works: number;
  revisions: number;
  current: ResolvedIsland;
  islands: Array<{
    island: ResolvedIsland;
    unlocked: boolean;
    blocker: Blocker | null;
    progress: IslandProgress;
  }>;
  visible: string[];
  features: Feature[];
}

export function overview(content: ResolvedContent, state: PlayerState): Overview {
  const visible = visibleIslands(content, state).map((i) => i.id);
  return {
    works: countWorks(content, state),
    revisions: countRevisions(content, state),
    current: currentIsland(content, state),
    islands: content.islands.map((island) => ({
      island,
      unlocked: isIslandUnlocked(content, state, island),
      blocker: islandBlocker(content, state, island),
      progress: islandProgress(island, state),
    })),
    visible,
    features: unlockedFeatures(content, state),
  };
}
